/** Indicatifs supportés par `PrepeersPhoneInput` (ordre : plus long d'abord). */
export const KNOWN_PHONE_PREFIXES: string[] = ['+352', '+33', '+32', '+41', '+1']

interface PhoneLengthRule {
  min: number
  max: number
  stripLeadingZero: boolean
}

const PHONE_LENGTH_RULES: Record<string, PhoneLengthRule> = {
  '+33': { min: 9, max: 9, stripLeadingZero: true },
  '+32': { min: 8, max: 9, stripLeadingZero: true },
  '+41': { min: 9, max: 9, stripLeadingZero: true },
  '+352': { min: 8, max: 11, stripLeadingZero: true },
  '+1': { min: 10, max: 10, stripLeadingZero: false },
}

export interface NormalizedPhone {
  prefix: string
  number: string
  full: string
}

/**
 * Découpe un numéro complet E.164 en indicatif + numéro local.
 * @param {string} fullPhone - Numéro complet (ex. "+33600000000").
 * @returns {{ prefix: string; number: string }} Indicatif pays et numéro local.
 */
export function splitFullPhone(fullPhone: string): { prefix: string; number: string } {
  const normalized: string = fullPhone.replace(/\s/g, '')
  if (normalized === '') return { prefix: '+33', number: '' }

  const matchedPrefix: string | undefined = KNOWN_PHONE_PREFIXES.find((prefix: string): boolean =>
    normalized.startsWith(prefix),
  )
  if (matchedPrefix !== undefined) {
    return { prefix: matchedPrefix, number: normalized.slice(matchedPrefix.length) }
  }

  return { prefix: '+33', number: normalized.replace(/^\+/, '') }
}

/**
 * Normalise indicatif + numéro local (supprime espaces, retire le 0 national après +33/+32…).
 * @param {string} prefix - Indicatif pays (ex. `+33`).
 * @param {string} number - Numéro local saisi.
 * @returns {NormalizedPhone} Parties normalisées et numéro E.164 complet.
 */
export function normalizePhoneParts(prefix: string, number: string): NormalizedPhone {
  const trimmedPrefix: string = prefix.trim() || '+33'
  let digits: string = number.replace(/\D/g, '')
  const rule: PhoneLengthRule | undefined = PHONE_LENGTH_RULES[trimmedPrefix]

  if (rule?.stripLeadingZero === true && digits.startsWith('0')) {
    digits = digits.slice(1)
  }

  return {
    prefix: trimmedPrefix,
    number: digits,
    full: `${trimmedPrefix}${digits}`,
  }
}

/**
 * Retourne la clé i18n d'erreur si le numéro est vide ou incomplet, sinon `null`.
 * @param {string} prefix - Indicatif pays.
 * @param {string} number - Numéro local saisi.
 * @returns {string | null} Clé i18n (`validation.required`, `validation.invalidPhone`) ou `null`.
 */
export function getPhoneValidationI18nKey(prefix: string, number: string): string | null {
  const { prefix: normalizedPrefix, number: normalizedNumber } = normalizePhoneParts(prefix, number)

  if (normalizedNumber === '') return 'validation.required'

  const rule: PhoneLengthRule | undefined = PHONE_LENGTH_RULES[normalizedPrefix]
  if (rule === undefined) {
    return normalizedNumber.length < 8 ? 'validation.invalidPhone' : null
  }

  if (normalizedNumber.length < rule.min || normalizedNumber.length > rule.max) {
    return 'validation.invalidPhone'
  }

  return null
}
